import { api } from '@/api';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import { useRouter } from 'vue-router';
import { RoleCatalogDto, type UserDto } from '@/api/generator';
import { useNotificationStore } from '@/stores/notificationStore.ts';
import { t } from 'i18next';
import { fullNameWithIuid } from '@/utils/helper.ts';
import { routenames } from '@/router/routenames.ts';
import { computed, onMounted, ref, readonly } from 'vue';
import { useLoading } from '@/utils/useLoading';
import { I18nKey } from '@/utils/typings';

export function useDeleteAccountWithConfirmation() {
  return useActionWithConfirmation({
    confirmedAction: async (id: number) =>
      await api.usersApi.deleteUser({
        id,
      }),
    confirmationOptions: {
      title: 'Really',
      text: 'Do you want to delete this User?',
    },
  });
}

export function useSendActivationLink() {
  const router = useRouter();

  const { action: sendActivationLink, loading } = useLoading(async (user: UserDto) => {
    const feedbackStore = useNotificationStore();
    const { success, result } = await api.usersApi.sendActivation({
      id: user.id,
    });
    if (success) {
      feedbackStore.success(
        t('SendingActivationSuccessfulMsg', {
          userFullNameWithIuid: fullNameWithIuid(user),
        }),
        t('SendingActivationSuccessful'),
      );
    } else {
      feedbackStore.error(
        t('SendingActivationFailedMsg', {
          userFullNameWithIuid: fullNameWithIuid(user),
        }),
        {
          actionTitle: t('TryAgain'),
          action: () => {
            router.push({
              name: routenames.accounts.details,
              params: { id: user.id },
            });
          },
        },
        t('SendingActivationFailed'),
      );
    }
    return { success, result };
  });

  return {
    sendActivationLink,
    loading,
  };
}

export function useRoles() {
  const roles = ref<RoleCatalogDto[]>();

  const { action: loadRoles, loading } = useLoading(async () => {
    const { result } = await api.rolesApi.getRolesCatalog();
    if (result) {
      // Split this line because of SonarQube
      roles.value = result;
      roles.value.sort((roleLeft, roleRight) =>
        (roleLeft.displayName ?? '').localeCompare(roleRight.displayName ?? ''),
      );
    }
  });

  const rolesDict = computed(() => {
    return roles.value?.reduce(
      (pre, role) => ({ ...pre, [role.id]: role }),
      {} as Record<number, RoleCatalogDto>,
    );
  });

  const rolesOptions = computed(() =>
    roles.value?.map((role) => ({
      title: role.type !== 'CustomRole' ? t(role.displayName as I18nKey) : role.displayName || '',
      value: role.id,
    })),
  );

  const getRoles = (roleIds?: number[] | null) => {
    if (!roleIds) {
      return [];
    }
    const rolesDictValue = rolesDict.value; // just to satisfy ts-compiler
    return rolesDictValue && roleIds.map((roleId) => rolesDictValue[roleId]);
  };

  // Pay attention: this need to be wrapped as computed when used in a setup function (because of roles-loading)
  const getRoleNames = (roleIds?: number[] | null) => {
    return getRoles(roleIds)
      ?.map((role) =>
        role.type !== 'CustomRole' ? t(role.displayName as I18nKey) : role.displayName || '',
      )
      .sort((roleNameLeft, roleNameRight) => roleNameLeft.localeCompare(roleNameRight));
  };

  onMounted(() => {
    loadRoles();
  });

  return {
    loading,
    loadRoles,
    getRoleNames,
    getRoles,
    roles: readonly(roles),
    rolesDict,
    rolesOptions,
  };
}
